import { useAuth } from "@/lib/contexts/auth.context";
import { KlausurService } from "@/lib/klausur/klausur.service";
import type { NavigationKlausurDTO } from "@/types/klausurEditor.types";
import type { KorrekturStudentKlausurDTO } from "@/features/korrektur/korrektur.types";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

/**
 * Core context value providing base klausur data and loading capabilities.
 * This is the foundation for other klausur-related providers.
 */
export interface KlausurCoreContextValue {
	/** Selected klausur id */
	klausurId: string;
	/** Basic klausur metadata */
	klausurMeta: { id: string; title: string } | null;
	/** Navigation data (aufgabenstellungen and sachverhalte) for sidebar */
	navigationKlausur: NavigationKlausurDTO | null;
	/** Full klausur structure for editor rendering */
	data: KorrekturStudentKlausurDTO | null;
	/** Loading state for detailed klausur */
	isLoading: boolean;
	/** Error state for detailed klausur */
	error: string | null;
	/** Organization ID from auth context */
	organizationId: string | undefined;
	/** Load klausur metadata and navigation structure */
	loadKlausurMeta: (klausurId: string) => Promise<void>;
	/** Load detailed klausur structure for editor */
	loadKlausurData: (klausurId: string) => Promise<void>;
	/** Reload both meta and data from backend */
	reloadKlausur: () => Promise<void>;
	/** Update navigation state (for optimistic updates) */
	setNavigationKlausur: React.Dispatch<
		React.SetStateAction<NavigationKlausurDTO | null>
	>;
	/** Update data state (for optimistic updates) */
	setData: React.Dispatch<
		React.SetStateAction<KorrekturStudentKlausurDTO | null>
	>;
}

/**
 * Context for core klausur data management.
 * Provides loading, state, and reload capabilities.
 */
const KlausurCoreContext = createContext<KlausurCoreContextValue | undefined>(
	undefined,
);

interface KlausurCoreProviderProps {
	children: React.ReactNode;
	klausurId: string;
}

/**
 * Core provider for klausur data management.
 * Handles loading klausur metadata, navigation structure, and detailed data.
 * Other providers can build on top of this to add CRUD operations.
 */
export function KlausurCoreProvider({
	children,
	klausurId,
}: KlausurCoreProviderProps) {
	const { currentOrganization } = useAuth();
	const [klausurMeta, setKlausurMeta] = useState<{
		id: string;
		title: string;
	} | null>(null);
	const [navigationKlausur, setNavigationKlausur] =
		useState<NavigationKlausurDTO | null>(null);
	const [data, setData] = useState<KorrekturStudentKlausurDTO | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Loads klausur metadata and builds navigation structure.
	 * Navigation is used for sidebar rendering with aufgabenstellungen and sachverhalte.
	 */
	const loadKlausurMeta = useCallback(
		async (id: string) => {
			const organizationId = currentOrganization?.organizationId;
			if (!organizationId || !id) return;

			const klausur = await KlausurService.getInstance().getKlausurById(
				organizationId,
				id,
			);
			setKlausurMeta({ id, title: klausur.title });

			// Build navigation structure from response
			const k = klausur as unknown as KorrekturStudentKlausurDTO;
			const aufgabenstellungen = k?.aufgabenstellungen ?? [];
			const nav: NavigationKlausurDTO = {
				id,
				title: klausur.title,
				aufgabenstellungen: (Array.isArray(aufgabenstellungen)
					? aufgabenstellungen
					: []
				).map((a) => ({
					id: a.id,
					title: a.title,
					index: a.index,
					sachverhalte: (Array.isArray(a.sachverhalte)
						? a.sachverhalte
						: []
					).map((s) => ({
						id: s.id,
						title: s.title,
						index: s.index,
					})),
				})),
			};
			setNavigationKlausur(nav);
		},
		[currentOrganization?.organizationId],
	);

	/**
	 * Loads the detailed klausur structure used by the editor.
	 * Normalizes backend response into the client rendering shape.
	 * Trusts backend invariants: ids/indexes are present and sorted.
	 */
	const loadKlausurData = useCallback(
		async (id: string) => {
			if (!id) return;
			const organizationId = currentOrganization?.organizationId;
			if (!organizationId) return;

			setIsLoading(true);
			setError(null);
			try {
				const orgKlausur = await KlausurService.getInstance().getKlausurById(
					organizationId,
					id,
				);
				setData(orgKlausur as unknown as KorrekturStudentKlausurDTO);
			} catch (e) {
				console.error(e);
				setError("Inhalt konnte nicht geladen werden.");
			} finally {
				setIsLoading(false);
			}
		},
		[currentOrganization?.organizationId],
	);

	/**
	 * Reloads both navigation/meta and full klausur data from backend.
	 * Useful after mutations to ensure local state matches server.
	 */
	const reloadKlausur = useCallback(async () => {
		await Promise.all([loadKlausurMeta(klausurId), loadKlausurData(klausurId)]);
	}, [klausurId, loadKlausurMeta, loadKlausurData]);

	// Initial load on mount and when klausurId changes
	useEffect(() => {
		loadKlausurMeta(klausurId);
		loadKlausurData(klausurId);
	}, [klausurId, loadKlausurMeta, loadKlausurData]);

	const value = useMemo(
		() => ({
			klausurId,
			klausurMeta,
			navigationKlausur,
			data,
			isLoading,
			error,
			organizationId: currentOrganization?.organizationId,
			loadKlausurMeta,
			loadKlausurData,
			reloadKlausur,
			setNavigationKlausur,
			setData,
		}),
		[
			klausurId,
			klausurMeta,
			navigationKlausur,
			data,
			isLoading,
			error,
			currentOrganization?.organizationId,
			loadKlausurMeta,
			loadKlausurData,
			reloadKlausur,
		],
	);

	return (
		<KlausurCoreContext.Provider value={value}>
			{children}
		</KlausurCoreContext.Provider>
	);
}

/**
 * Hook to access core klausur context.
 * Must be used within KlausurCoreProvider.
 */
export function useKlausurCore() {
	const ctx = useContext(KlausurCoreContext);
	if (!ctx)
		throw new Error("useKlausurCore must be used within KlausurCoreProvider");
	return ctx;
}
